import { useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Trash2, ChevronLeft, ChevronRight, Loader2, Users } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { FIELD_TYPES } from './helpers';
import { usePdfDocument, PdfPageCanvas } from './PdfPageCanvas';

// Per-signer field colors — signer 1 keeps the classic emerald, others get
// distinct hues so multi-signer layouts read at a glance.
const SIGNER_STYLES = [
  { border: 'border-emerald-500', bg: 'bg-emerald-100/40 dark:bg-emerald-900/30', text: 'text-emerald-800 dark:text-emerald-200', chip: 'bg-emerald-600' },
  { border: 'border-sky-500', bg: 'bg-sky-100/40 dark:bg-sky-900/30', text: 'text-sky-800 dark:text-sky-200', chip: 'bg-sky-600' },
  { border: 'border-violet-500', bg: 'bg-violet-100/40 dark:bg-violet-900/30', text: 'text-violet-800 dark:text-violet-200', chip: 'bg-violet-600' },
  { border: 'border-amber-500', bg: 'bg-amber-100/40 dark:bg-amber-900/30', text: 'text-amber-800 dark:text-amber-200', chip: 'bg-amber-600' },
  { border: 'border-rose-500', bg: 'bg-rose-100/40 dark:bg-rose-900/30', text: 'text-rose-800 dark:text-rose-200', chip: 'bg-rose-600' },
];
const signerStyle = (idx) => SIGNER_STYLES[((idx || 1) - 1) % SIGNER_STYLES.length];

/**
 * Interactive PDF + field-placement overlay used by the New Request Wizard
 * AND the Template editor. Renders the PDF page-by-page via pdf.js with page
 * navigation. Fields carry `page` and `signer_index`; drag to move, drag the
 * corner handle to resize, click the S# chip to reassign to the next signer.
 */
export function FieldPlacementEditor({ pdfDataUri, fields, onChange, signers = [] }) {
  const overlayRef = useRef(null);
  const [activeTool, setActiveTool] = useState(null);
  const [hoverPos, setHoverPos] = useState(null);
  const [dragging, setDragging] = useState(null);  // { idx (global), offX, offY }
  const [resizing, setResizing] = useState(null);  // { idx (global) }
  const [currentPage, setCurrentPage] = useState(1);
  const [activeSigner, setActiveSigner] = useState(1);
  const { pdf, numPages } = usePdfDocument(pdfDataUri);

  const multiSigner = signers.length > 1;
  const signerLabel = (i) => {
    const s = signers[i - 1];
    const nm = (s?.name || s?.email || '').trim();
    return nm ? `${nm}` : `Signer ${i}`;
  };

  const visibleFields = useMemo(
    () => fields
      .map((f, idx) => ({ f, idx }))
      .filter(({ f }) => (f.page || 1) === currentPage),
    [fields, currentPage],
  );

  const pagesWithFields = useMemo(() => {
    const counts = {};
    fields.forEach((f) => {
      const p = f.page || 1;
      counts[p] = (counts[p] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([p, n]) => ({ page: Number(p), count: n }))
      .sort((a, b) => a.page - b.page);
  }, [fields]);

  const goToPage = (p) => {
    const clamped = Math.max(1, Math.min(numPages || 1, p));
    setCurrentPage(clamped);
    setHoverPos(null);
    setDragging(null);
    setResizing(null);
  };

  const handleMouseDown = (e, globalIdx) => {
    if (activeTool) return;
    e.stopPropagation();
    const rect = overlayRef.current.getBoundingClientRect();
    const fld = fields[globalIdx];
    const offX = (e.clientX - rect.left) / rect.width - fld.x;
    const offY = (e.clientY - rect.top) / rect.height - fld.y;
    setDragging({ idx: globalIdx, offX, offY });
  };

  const handleResizeDown = (e, globalIdx) => {
    e.stopPropagation();
    e.preventDefault();
    setResizing({ idx: globalIdx });
  };

  const handleMouseMove = (e) => {
    if (!overlayRef.current) return;
    const rect = overlayRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    if (resizing) {
      const next = [...fields];
      const f = next[resizing.idx];
      next[resizing.idx] = {
        ...f,
        width: Math.max(0.04, Math.min(1 - f.x, x - f.x)),
        height: Math.max(0.015, Math.min(1 - f.y, y - f.y)),
      };
      onChange(next);
    } else if (dragging) {
      const next = [...fields];
      const f = next[dragging.idx];
      next[dragging.idx] = {
        ...f,
        x: Math.max(0, Math.min(1 - f.width, x - dragging.offX)),
        y: Math.max(0, Math.min(1 - f.height, y - dragging.offY)),
      };
      onChange(next);
    } else if (activeTool) {
      const def = FIELD_TYPES.find(f => f.id === activeTool);
      setHoverPos({
        x: Math.max(0, Math.min(1 - def.defaultW, x - def.defaultW / 2)),
        y: Math.max(0, Math.min(1 - def.defaultH, y - def.defaultH / 2)),
        w: def.defaultW,
        h: def.defaultH,
      });
    }
  };

  const handleMouseUp = () => { setDragging(null); setResizing(null); };
  const handleMouseLeave = () => { setHoverPos(null); setDragging(null); setResizing(null); };

  const handlePlace = (e) => {
    if (!activeTool || dragging || resizing) return;
    const rect = overlayRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const def = FIELD_TYPES.find(f => f.id === activeTool);
    const id = `fld_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    onChange([...fields, {
      field_id: id,
      type: activeTool,
      page: currentPage,
      x: Math.max(0, Math.min(1 - def.defaultW, x - def.defaultW / 2)),
      y: Math.max(0, Math.min(1 - def.defaultH, y - def.defaultH / 2)),
      width: def.defaultW,
      height: def.defaultH,
      required: true,
      label: def.label,
      signer_index: multiSigner ? activeSigner : 1,
    }]);
    setActiveTool(null);
    setHoverPos(null);
  };

  const removeField = (globalIdx) => onChange(fields.filter((_, i) => i !== globalIdx));

  const cycleSigner = (globalIdx) => {
    if (!multiSigner) return;
    const next = [...fields];
    const f = next[globalIdx];
    const cur = f.signer_index || 1;
    next[globalIdx] = { ...f, signer_index: (cur % signers.length) + 1 };
    onChange(next);
  };

  const updateDefaultValue = (globalIdx, value) => {
    const next = [...fields];
    next[globalIdx] = { ...next[globalIdx], default_value: value };
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Pick a tool, then click on the PDF to place:</span>
        {FIELD_TYPES.map(t => {
          const Icon = t.icon;
          const isActive = activeTool === t.id;
          return (
            <Button
              key={t.id}
              size="sm"
              variant={isActive ? 'default' : 'outline'}
              onClick={() => { setActiveTool(isActive ? null : t.id); setHoverPos(null); }}
              className="gap-1.5"
              data-testid={`field-tool-${t.id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </Button>
          );
        })}
        {activeTool && (
          <span className="text-xs text-emerald-700 dark:text-emerald-300">
            ✋ Move cursor over the PDF to see the placement preview, then click to drop.
          </span>
        )}
        {!activeTool && fields.length > 0 && (
          <span className="text-xs text-muted-foreground">
            💡 Drag to move · drag the corner dot to resize{multiSigner ? ' · click the S# chip to reassign the signer' : ''}.
          </span>
        )}
      </div>

      {/* Multi-signer: pick who the NEXT placed field belongs to */}
      {multiSigner && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="signer-assignment-bar">
          <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
            <Users className="w-3 h-3" /> Placing fields for:
          </span>
          {signers.map((s, i) => {
            const idx = i + 1;
            const st = signerStyle(idx);
            const sel = activeSigner === idx;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => setActiveSigner(idx)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                  sel ? `${st.chip} border-transparent text-white` : `bg-white dark:bg-background ${st.border} ${st.text}`
                }`}
                data-testid={`assign-signer-${idx}`}
              >
                <span className={`inline-block w-2 h-2 rounded-full ${sel ? 'bg-white/80' : st.chip}`} />
                S{idx} · {signerLabel(idx) || `Signer ${idx}`}
              </button>
            );
          })}
        </div>
      )}

      {/* Page navigation — prev / next + jump-to-page dropdown */}
      {numPages > 1 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="pdf-page-nav">
          <Button size="sm" variant="outline" onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1} aria-label="Previous page" data-testid="pdf-prev-page-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Select value={String(currentPage)} onValueChange={(v) => goToPage(Number(v))}>
            <SelectTrigger className="w-[150px] h-8" data-testid="pdf-page-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: numPages }, (_, i) => i + 1).map((p) => (
                <SelectItem key={p} value={String(p)}>Page {p} of {numPages}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= numPages} aria-label="Next page" data-testid="pdf-next-page-btn">
            <ChevronRight className="w-4 h-4" />
          </Button>
          {pagesWithFields.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 ml-1">
              <span className="text-[11px] text-muted-foreground">Fields on:</span>
              {pagesWithFields.map(({ page, count }) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => goToPage(page)}
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
                    page === currentPage
                      ? 'bg-emerald-600 border-emerald-600 text-white'
                      : 'border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-950/40'
                  }`}
                  data-testid={`page-chip-${page}`}
                >
                  p{page} · {count}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {!pdf ? (
        <div className="flex items-center justify-center w-full border rounded bg-muted/30" style={{ aspectRatio: '612 / 792' }}>
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <PdfPageCanvas pdf={pdf} pageNumber={currentPage}>
          <div
            ref={overlayRef}
            onClick={handlePlace}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            className={`absolute inset-0 ${activeTool ? 'cursor-crosshair' : 'cursor-default'}`}
            data-testid="pdf-placement-overlay"
          >
            {hoverPos && activeTool && (
              <div
                className="absolute border-2 border-emerald-500/60 bg-emerald-200/30 dark:bg-emerald-900/30 rounded pointer-events-none flex items-center justify-center text-[10px] font-mono text-emerald-700 dark:text-emerald-200"
                style={{
                  left: `${hoverPos.x * 100}%`,
                  top: `${hoverPos.y * 100}%`,
                  width: `${hoverPos.w * 100}%`,
                  height: `${hoverPos.h * 100}%`,
                }}
                aria-hidden
              >
                {activeTool}
              </div>
            )}
            {visibleFields.map(({ f, idx }) => {
              const st = signerStyle(f.signer_index || 1);
              return (
                <div
                  key={f.field_id || idx}
                  onMouseDown={(e) => handleMouseDown(e, idx)}
                  className={`absolute border-2 ${st.border} ${st.bg} rounded text-[10px] flex items-center justify-between px-1 group ${dragging?.idx === idx ? 'cursor-grabbing' : 'cursor-grab'}`}
                  style={{
                    left: `${f.x * 100}%`,
                    top: `${f.y * 100}%`,
                    width: `${f.width * 100}%`,
                    height: `${f.height * 100}%`,
                  }}
                  data-testid={`placed-field-${idx}`}
                  title={f.type === 'text'
                    ? 'Drag to move; drag corner to resize; click trash to remove; type into the field to lock custom text'
                    : 'Drag to move; drag corner to resize; click trash to remove'}
                >
                  {multiSigner && (
                    <button
                      type="button"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); cycleSigner(idx); }}
                      className={`${st.chip} text-white rounded px-1 mr-1 shrink-0 text-[9px] font-bold leading-4`}
                      title={`Assigned to ${signerLabel(f.signer_index || 1)} — click to switch`}
                      data-testid={`field-signer-chip-${idx}`}
                    >
                      S{f.signer_index || 1}
                    </button>
                  )}
                  {f.type === 'text' ? (
                    <input
                      type="text"
                      value={f.default_value || ''}
                      onChange={(e) => updateDefaultValue(idx, e.target.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      placeholder="Signer's name (or type custom)"
                      className={`flex-1 min-w-0 bg-transparent border-none outline-none font-mono text-[10px] ${st.text} placeholder:opacity-50 px-0.5`}
                      data-testid={`text-default-${idx}`}
                    />
                  ) : (
                    <span className={`truncate font-mono ${st.text} select-none`}>
                      {f.type.charAt(0).toUpperCase() + f.type.slice(1)}
                    </span>
                  )}
                  <button
                    type="button"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(ev) => { ev.stopPropagation(); removeField(idx); }}
                    className="opacity-60 group-hover:opacity-100 hover:text-rose-600 shrink-0"
                    aria-label={`Remove ${f.type} field`}
                    data-testid={`remove-field-${idx}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                  {/* Corner resize handle */}
                  <div
                    onMouseDown={(e) => handleResizeDown(e, idx)}
                    className={`absolute -bottom-1.5 -right-1.5 w-3 h-3 rounded-full ${st.chip} border-2 border-white dark:border-zinc-900 cursor-nwse-resize opacity-0 group-hover:opacity-100 ${resizing?.idx === idx ? 'opacity-100 scale-125' : ''} transition-opacity`}
                    title="Drag to resize"
                    data-testid={`resize-handle-${idx}`}
                  />
                </div>
              );
            })}
          </div>
        </PdfPageCanvas>
      )}

      <p className="text-[11px] text-muted-foreground">
        {fields.length === 0
          ? 'Pick a tool above and click on the PDF to place at least one field.'
          : `${fields.length} field${fields.length === 1 ? '' : 's'} placed${numPages > 1 ? ` across ${pagesWithFields.length} page${pagesWithFields.length === 1 ? '' : 's'}` : ''}. Drag to move · corner dot to resize.`}
      </p>
    </div>
  );
}

export default FieldPlacementEditor;
